import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Chip,
  InputAdornment,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  DescriptionOutlined,
  Search,
  SlideshowOutlined,
  VisibilityOutlined,
} from '@mui/icons-material';
import { EmptyState, IconTile } from '../pagekit';
import { isPresentationTemplateType } from '../documents/documentMeta';

/**
 * Selectable template card (radio semantics).
 * @param {object} props - Props.
 * @param {object} props.template - Template.
 * @param {object|undefined} props.quality - analyzeTemplateQuality result (document templates).
 * @param {boolean} props.selected - Whether it is the chosen template.
 * @param {function(): void} props.onSelect - Choose it.
 * @param {function(): void} props.onPreview - Preview it.
 * @returns {JSX.Element} Card.
 */
function TemplateOption({ template, quality, selected, onSelect, onPreview }) {
  const deck = isPresentationTemplateType(template.templateType);
  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid',
        borderColor: selected ? 'var(--q-accent)' : 'divider',
        borderRadius: 'var(--q-radius-xl)',
        bgcolor: selected ? 'var(--q-accent-tint)' : 'background.paper',
        boxShadow: selected ? '0 0 0 3px var(--q-focus-halo)' : 'none',
        transition: 'border-color 120ms ease, box-shadow 120ms ease',
        '&:hover': { borderColor: selected ? 'var(--q-accent)' : 'var(--q-border-hover)' },
      }}
    >
      <ButtonBase
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          textAlign: 'left',
          gap: 1.5,
          p: 1.75,
          pb: 1,
          borderRadius: 'var(--q-radius-xl)',
          width: '100%',
          '&.Mui-focusVisible': { outline: '2px solid var(--q-focus-ring)', outlineOffset: 2 },
        }}
      >
        <IconTile tone={deck ? 'neutral' : 'accent'} size={34}>
          {deck ? <SlideshowOutlined /> : <DescriptionOutlined />}
        </IconTile>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, lineHeight: 1.35, overflowWrap: 'anywhere' }}>
            {template.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="p">
            {deck && 'PowerPoint'}
            {!deck && (quality ? `Word · ${quality.metrics.promptCount ?? 0} AI field${quality.metrics.promptCount === 1 ? '' : 's'}` : 'Word')}
          </Typography>
          {template.description && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
            >
              {template.description}
            </Typography>
          )}
        </Box>
        {selected && <CheckCircle sx={{ color: 'var(--q-accent)', fontSize: 20, flex: 'none' }} aria-hidden />}
      </ButtonBase>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.75, pb: 1.25, mt: 'auto', flexWrap: 'wrap' }}>
        {quality && <Chip size="small" color={quality.color} label={`${quality.label} · ${quality.score}`} />}
        {!quality && deck && <Chip size="small" label="Slides" />}
        {template.status === 'draft' && <Chip size="small" color="warning" label="Draft" />}
        <Button
          size="small"
          startIcon={<VisibilityOutlined />}
          onClick={onPreview}
          sx={{ ml: 'auto' }}
          aria-label={`Preview ${template.name}`}
        >
          Preview
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Step 1: choose a template (searchable, filterable by type).
 * @param {object} props - Props.
 * @param {Array<object>} props.templates - All templates.
 * @param {Record<string, object>} props.qualityById - Quality results for document templates.
 * @param {string} props.value - Selected template id.
 * @param {function(string): void} props.onChange - Select a template.
 * @param {function(object): void} props.onPreview - Preview a template.
 * @param {function(): void} props.onCreateTemplate - Go create a template (empty state).
 * @returns {JSX.Element} Step body.
 */
export default function TemplateStep({ templates, qualityById, value, onChange, onPreview, onCreateTemplate }) {
  const [query, setQuery] = useState('');
  const [type, setType] = useState('all');

  const counts = useMemo(() => {
    const decks = templates.filter((t) => isPresentationTemplateType(t.templateType)).length;
    return { all: templates.length, documents: templates.length - decks, presentations: decks };
  }, [templates]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return templates.filter((t) => {
      const deck = isPresentationTemplateType(t.templateType);
      if (type === 'documents' && deck) return false;
      if (type === 'presentations' && !deck) return false;
      if (!q) return true;
      return `${t.name || ''} ${t.description || ''}`.toLowerCase().includes(q);
    });
  }, [templates, query, type]);

  if (templates.length === 0) {
    return (
      <EmptyState
        inky
        compact
        title="No templates yet"
        description="Templates hold the fixed structure and the AI fields Inky fills. Create one first."
        actions={<Button variant="contained" onClick={onCreateTemplate}>Create a template</Button>}
      />
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <TextField
          placeholder="Search templates"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ flex: '1 1 220px' }}
          inputProps={{ 'aria-label': 'Search templates' }}
          InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
        />
        {counts.presentations > 0 && counts.documents > 0 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={type}
            onChange={(e, next) => next && setType(next)}
            aria-label="Template type"
          >
            <ToggleButton value="all">All {counts.all}</ToggleButton>
            <ToggleButton value="documents">Documents {counts.documents}</ToggleButton>
            <ToggleButton value="presentations">Slides {counts.presentations}</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>
      {visible.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          No templates match. Try another search.
        </Typography>
      ) : (
        <Box
          role="radiogroup"
          aria-label="Templates"
          sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 250px), 1fr))', gap: 1.5 }}
        >
          {visible.map((template) => (
            <TemplateOption
              key={template.id}
              template={template}
              quality={qualityById[template.id]}
              selected={template.id === value}
              onSelect={() => onChange(template.id)}
              onPreview={() => onPreview(template)}
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
